import React, { useEffect, useState, useCallback } from 'react';
import { Service } from './Service';
import { Row, Col, Button, Input, Label, Card, CardHeader, CardBody, Badge, Spinner } from 'reactstrap';

const Products = () => {
    const service = React.useMemo(() => new Service(), []);
    const [products, setProducts] = useState([]);
    const [product, setProduct] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            const data = await service.getProducts();
            setProducts(data || []);
            if (data && data.length > 0) {
                setProduct(prev => {
                    if (prev && prev.id) {
                        return data.find(p => p.id === prev.id) || data[0];
                    }
                    return data[0];
                });
            }
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const addProduct = async () => {
        try {
            setLoading(true);
            const newProduct = {
                code: `PRD-${Date.now().toString().slice(-4)}`,
                title: 'Νέο Προϊόν',
                description: ''
            };

            const createdProduct = await service.addProduct(newProduct);
            setProducts([createdProduct, ...products]);
            setProduct(createdProduct);
        } catch (error) {
            console.error('Error adding product:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveProduct = async () => {
        if (!product) return;
        try {
            setLoading(true);
            const updatedProduct = await service.updateProduct(product);
            setProducts(products.map(p => p.id === product.id ? updatedProduct : p));
            setProduct(updatedProduct);
        } catch (error) {
            console.error('Error saving product:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το προϊόν;')) return;
        try {
            setLoading(true);
            const success = await service.deleteProduct(id);
            if (success) {
                const nextProducts = products.filter(p => p.id !== id);
                setProducts(nextProducts);
                setProduct(nextProducts.length > 0 ? nextProducts[0] : null);
            }
        } catch (error) {
            console.error('Error deleting product:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(p => 
        !search || 
        ((p.code && p.code.toLowerCase().includes(search.toLowerCase())) || 
         (p.title && p.title.toLowerCase().includes(search.toLowerCase())) || 
         (p.description && p.description.toLowerCase().includes(search.toLowerCase())))
    );

    return (
        <Row className="g-4">
            <Col lg="4" md="5">
                <div className="ek-card mb-0">
                    <div className="ek-card-header">
                        <h5>
                            <i className="bi bi-box-seam text-primary"></i> Προϊόντα
                        </h5>
                        <Badge color="info" pill>{products.length}</Badge>
                    </div>
                    <div className="ek-card-body">
                        <Button onClick={addProduct} block color="primary" className="mb-3">
                            <i className="bi bi-plus-lg"></i> Προσθήκη Προϊόντος
                        </Button>

                        <div className="ek-search-box">
                            <i className="bi bi-search"></i>
                            <Input
                                type="search"
                                className="ek-search-input"
                                placeholder="Αναζήτηση με τίτλο ή κωδικό..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="scrollable-list">
                            {loading && products.length === 0 ? (
                                <div className="text-center py-4">
                                    <Spinner size="sm" color="primary" />
                                    <span className="ms-2 text-muted">Φόρτωση...</span>
                                </div>
                            ) : filteredProducts.length === 0 ? (
                                <div className="ek-empty-state py-4">
                                    <i className="bi bi-inbox fs-2"></i>
                                    <p className="small text-muted mb-0">Δεν βρέθηκαν προϊόντα</p>
                                </div>
                            ) : (
                                <div className="ek-list-group">
                                    {filteredProducts.map(p => (
                                        <div
                                            key={p.id}
                                            className={`ek-list-item ${product && product.id === p.id ? 'active' : ''}`}
                                            onClick={() => setProduct(p)}
                                        >
                                            <div>
                                                <div className="ek-list-item-title">{p.title || 'Χωρίς Τίτλο'}</div>
                                                <div className="ek-list-item-sub">
                                                    <i className="bi bi-upc-scan"></i>
                                                    <code>{p.code || 'Χωρίς Κωδικό'}</code>
                                                </div>
                                            </div>
                                            <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Col>

            <Col lg="8" md="7">
                {product ? (
                    <Card className="ek-card">
                        <CardHeader className="ek-card-header">
                            <h5>
                                <i className="bi bi-pencil-square text-primary"></i> 
                                {product.title || 'Επεξεργασία Προϊόντος'}
                            </h5>
                            {product.code && <Badge className="badge-brand">{product.code}</Badge>}
                        </CardHeader>
                        <CardBody className="ek-card-body">
                            <div className="ek-form-group mb-3">
                                <Label className="ek-label">
                                    <i className="bi bi-upc"></i> Κωδικός Προϊόντος
                                </Label>
                                <Input
                                    value={product.code || ''}
                                    placeholder="π.χ. PRD-001"
                                    onChange={(e) => setProduct({ ...product, code: e.target.value })}
                                />
                            </div>

                            <div className="ek-form-group mb-3">
                                <Label className="ek-label">
                                    <i className="bi bi-tag"></i> Τίτλος Προϊόντος
                                </Label>
                                <Input
                                    value={product.title || ''}
                                    placeholder="π.χ. Ψωμί Χωριάτικο 500gr"
                                    onChange={(e) => setProduct({ ...product, title: e.target.value })}
                                />
                            </div>

                            <div className="ek-form-group mb-4">
                                <Label className="ek-label">
                                    <i className="bi bi-card-text"></i> Περιγραφή
                                </Label>
                                <Input
                                    type="textarea"
                                    rows="4"
                                    value={product.description || ''}
                                    placeholder="Προαιρετική περιγραφή ή παρατηρήσεις..."
                                    onChange={(e) => setProduct({ ...product, description: e.target.value })}
                                />
                            </div>

                            <div className="d-flex gap-2 pt-2 border-top">
                                <Button onClick={saveProduct} color="primary" disabled={loading}>
                                    <i className="bi bi-check2-circle"></i> Αποθήκευση
                                </Button>
                                <Button onClick={() => deleteProduct(product.id)} color="danger" disabled={loading}>
                                    <i className="bi bi-trash3"></i> Διαγραφή
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                ) : (
                    <div className="ek-card">
                        <div className="ek-empty-state">
                            <i className="bi bi-box-seam"></i>
                            <h4>Επιλέξτε ένα προϊόν</h4>
                            <p>Επιλέξτε ένα προϊόν από την αριστερή λίστα για επεξεργασία ή πατήστε "Προσθήκη Προϊόντος" για νέο.</p>
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    );
};

export { Products };
export default Products;
